import { useContext, useEffect, useState, ReactNode } from 'react';
import { Controller, UseFormReturn, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    GridProps,
    InputAdornment,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Select,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, parseISO, isValid } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import {
    SELECT_CITIES,
    SELECT_CUSTOMERS,
    SELECT_MSHIPS,
    SELECT_PROVINCES,
    SELECT_TPV_CONFIGURATION,
} from '../lib/queryKeys';
import useCitiesService from '../services/CitiesService';
import useCustomersService from '../services/CustomersService';
import useTpvsService from '../services/TpvsService';
import { SttButtonSave } from './buttons/SttButtonSave';
import { SttButtonDelete } from './buttons/SttButtonDelete';
import { SttButtonAccept } from './buttons/SttButtonAccept';
import { SttButtonRecover } from './buttons/SttButtonRecover';
import { SttButton } from './buttons/SttButton';
import { usePermission } from '../lib/usePermission';
import { PermissionRequirement } from '../lib/permissions';
import { AppContext } from './AppContext';
import useScsService from '../services/ScsService';
import { checkOffTag } from '../lib/utils';
import useProvincesService from '../services/ProvincesService';
import { SttOpenStreetMap, SelectGroupField } from './all';
import { SttSelectCountry } from './selectors/SttSelectCountry';

const textHeight = 56;
const textAreaHeightPerRow = 24;

// Field types
type FieldType =
    | 'textinput'
    | 'textInput'
    | 'numberinput'
    | 'numberInput'
    | 'select'
    | 'textarea'
    | 'textArea'
    | 'date'
    | 'check'
    | 'city'
    | 'price'
    | 'customer'
    | 'tpvconf'
    | 'tpvConf'
    | 'label'
    | 'time'
    | 'multipleselectcheck'
    | 'multipleSelectCheck'
    | 'months'
    | 'weekdays'
    | 'group'
    | 'mship'
    | 'province'
    | 'country'
    | 'map';

type FieldOption = {
    caption?: string;
    label?: string;
    value: string | number;
};

type FieldRules = {
    required?: boolean | string;
    pattern?: {
        value: RegExp;
        message: string;
    };
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    validate?: (value: unknown) => boolean | string;
};

export type FormField = {
    name: string;
    caption?: string;
    type?: FieldType;
    value?: unknown;
    show?: boolean | number;
    disabled?: boolean;
    rules?: FieldRules;
    options?: FieldOption[];
    rows?: number;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    idProvince?: number;
    formatedText?: boolean;
    tinyLabels?: boolean;
    disableClearable?: boolean;
    onEnterKey?: (value: unknown) => void;
    InputProps?: Record<string, unknown>;
    [key: string]: unknown;
};

type ButtonType = 'save' | 'delete' | 'accept' | 'recover' | 'component';

export type FormButton = {
    type?: ButtonType;
    caption?: string;
    show?: boolean | number;
    confirmation?: boolean;
    onClick?: (e?: React.BaseSyntheticEvent) => void | Promise<void>;
    component?: ReactNode;
    /** Permiso v8 que hace falta para ver el botón (p. ej. `<módulo>.modify`). */
    permission?: PermissionRequirement;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
};

type SttValidatedFormProps = {
    form: UseFormReturn<any>;
    loadingData?: boolean;
    fields?: FormField[];
    buttons?: FormButton[];
    containerProps?: Partial<GridProps>;
};

// Autocomplete component types
type AutocompleteOption = {
    id?: number;
    name?: string;
    fullName?: string;
    img?: { url: string };
    user?: { login: string };
    amount?: number;
};

type AutocompleteComponentProps = {
    name: string;
    caption: string;
    value: AutocompleteOption | null;
    errors: FieldErrors;
    onChange: (data: AutocompleteOption | null) => void;
    idProvince?: number;
    [key: string]: unknown;
};

// Query response type for lists
type ListResponse<T> = {
    rows: T[];
    count?: number;
    pages?: number;
};

// TODO - Viendo que el componente está creciendo habría que sacar todas las funciones de los subcomponentes en ficheros separados.

function SttAutocompleteCity({
    name,
    caption,
    value,
    errors,
    onChange,
    idProvince,
    ...rest
}: AutocompleteComponentProps) {
    const citiesService = useCitiesService();
    const [queryParams, setQueryParams] = useState({
        rows: 100,
        name: '',
        idProvince,
    });

    const citiesQuery = useQuery({
        queryKey: [SELECT_CITIES, queryParams],
        queryFn: () =>
            citiesService.getList<ListResponse<AutocompleteOption>>(
                queryParams,
            ),
    });

    useEffect(() => {
        setQueryParams((prev: any) => ({ ...prev, idProvince }));
    }, [idProvince]);

    return (
        <Autocomplete
            options={
                citiesQuery?.data?.rows
                    ? citiesQuery?.data?.rows
                    : [
                          {
                              id: value?.id,
                              name: value?.name,
                          },
                      ]
            }
            loading={citiesQuery?.isLoading}
            getOptionLabel={(option: AutocompleteOption) =>
                option?.name ? option?.name : ''
            }
            isOptionEqualToValue={(
                option: AutocompleteOption,
                val: AutocompleteOption,
            ) => option?.id === val?.id}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    label={caption}
                    size="small"
                    variant="outlined"
                    error={!!errors[name]}
                    helperText={
                        errors[name] ? String(errors[name]?.message) : null
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {citiesQuery?.isLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onChange={(_e, data) => onChange(data)}
            onInputChange={(_event, newInputValue) =>
                setQueryParams({
                    ...queryParams,
                    name: newInputValue,
                })
            }
            value={value || null}
            style={{ paddingTop: 16 }}
            {...rest}
        />
    );
}

function SttAutocompleteCustomer({
    name,
    caption,
    value,
    errors,
    onChange,
    ...rest
}: AutocompleteComponentProps) {
    const customersService = useCustomersService();
    const [queryParams, setQueryParams] = useState({
        rows: 100,
        name: '',
    });

    const customersQuery = useQuery({
        queryKey: [SELECT_CUSTOMERS, queryParams],
        queryFn: () =>
            customersService.getList<ListResponse<AutocompleteOption>>(
                queryParams,
            ),
    });

    return (
        <Autocomplete
            options={
                customersQuery?.data?.rows
                    ? customersQuery?.data?.rows
                    : [
                          {
                              id: value?.id,
                              name: value?.name,
                          },
                      ]
            }
            loading={customersQuery?.isLoading}
            getOptionLabel={(option: AutocompleteOption) =>
                option?.name ? option?.name : ''
            }
            // Estas comprobaciones se hacen para que no aparezcan warnings, cuando dejamos sin
            // cliente el back-end devuelve customer con id === 0 y no corresponde con ninguno de
            // la lista, pero debemos dar la posibilidad de dejarlo en blanco.
            isOptionEqualToValue={(
                option: AutocompleteOption,
                val: AutocompleteOption,
            ) => val?.id === 0 || option?.id === val?.id}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    label={caption}
                    size="small"
                    variant="outlined"
                    error={!!errors[name]}
                    helperText={
                        errors[name] ? String(errors[name]?.message) : null
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {customersQuery?.isLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onChange={(_e, data) => onChange(data)}
            onInputChange={(_event, newInputValue) =>
                setQueryParams({
                    ...queryParams,
                    name: newInputValue,
                })
            }
            value={value || null}
            style={{ paddingTop: 16 }}
            {...rest}
        />
    );
}

function SttAutocompleteTpvConfiguration({
    name,
    caption,
    value,
    errors,
    onChange,
    ...rest
}: AutocompleteComponentProps) {
    const tpvsService = useTpvsService();
    const [queryParams, setQueryParams] = useState({
        rows: 100,
        name: '',
    });

    const tpvsConfigurationsQuery = useQuery({
        queryKey: [SELECT_TPV_CONFIGURATION, queryParams],
        queryFn: () =>
            tpvsService.getTpvsConfigurations(queryParams) as Promise<
                ListResponse<AutocompleteOption>
            >,
    });

    return (
        <Autocomplete
            options={
                tpvsConfigurationsQuery?.data?.rows
                    ? tpvsConfigurationsQuery?.data?.rows
                    : [
                          {
                              id: value?.id,
                              name: value?.name,
                          },
                      ]
            }
            loading={tpvsConfigurationsQuery?.isLoading}
            getOptionLabel={(option: AutocompleteOption) =>
                option?.name ? option?.name : ''
            }
            isOptionEqualToValue={(
                option: AutocompleteOption,
                val: AutocompleteOption,
            ) => option?.id === val?.id}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    label={caption}
                    size="small"
                    variant="outlined"
                    error={!!errors[name]}
                    helperText={
                        errors[name] ? String(errors[name]?.message) : null
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {tpvsConfigurationsQuery?.isLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onChange={(_e, data) => onChange(data)}
            onInputChange={(_event, newInputValue) =>
                setQueryParams({
                    ...queryParams,
                    name: newInputValue,
                })
            }
            value={value || null}
            style={{ paddingTop: 16 }}
            {...rest}
        />
    );
}

type SttAutocompleteGroupProps = {
    name: string;
    caption: string;
    value: { id: number; name: string } | null | undefined;
    onChange: (value: unknown) => void;
    [key: string]: unknown;
};

function SttAutocompleteGroup({
    name,
    caption,
    value,
    onChange,
    ...rest
}: SttAutocompleteGroupProps) {
    return (
        <SelectGroupField
            caption={caption}
            name={name}
            defVal={value}
            onChange={(prop: { value: unknown }) => {
                onChange(prop.value);
            }}
            {...rest}
        />
    );
}

type SttAutocompleteCountryProps = {
    name: string;
    caption: string;
    defValue: { id: number; name: string } | null | undefined;
    value: { id: number; name: string } | null | undefined;
    onChange: (value: unknown) => void;
    [key: string]: unknown;
};

function SttAutocompleteCountry({
    name,
    caption,
    defValue,
    value,
    onChange,
    ...rest
}: SttAutocompleteCountryProps) {
    return (
        <SttSelectCountry
            caption={caption}
            name={name}
            defVal={defValue}
            value={value ?? { id: 0, name: '' }}
            onChange={(prop: { value: unknown }) => {
                onChange(prop.value);
            }}
            {...rest}
        />
    );
}

type SttAutocompleteMapProps = {
    value: { mapX?: number; mapY?: number } | null;
    defValue: { mapX?: number; mapY?: number } | null;
    onChange: (value: { mapX: number; mapY: number }) => void;
};

function SttAutocompleteMap({
    value,
    defValue,
    onChange,
}: SttAutocompleteMapProps) {
    return (
        <SttOpenStreetMap
            height="300px"
            latLng={{
                lat: value?.mapX ?? defValue?.mapX ?? 0,
                lng: value?.mapY ?? defValue?.mapY ?? 0,
            }}
            searchEnabled
            onChange={(prop: { lat: number; lng: number }) => {
                onChange({ mapX: prop.lat, mapY: prop.lng });
            }}
        />
    );
}

function SttAutocompleteScsMships({
    name,
    caption,
    value,
    errors,
    onChange,
    ...rest
}: AutocompleteComponentProps) {
    const { t: translate } = useTranslation();
    const { sc } = useContext(AppContext)!;
    const scsService = useScsService();
    const [queryParams, setQueryParams] = useState({
        rows: 100,
        name: '',
    });

    const scsMshipsQuery = useQuery({
        queryKey: [SELECT_MSHIPS, queryParams],
        queryFn: () =>
            scsService.getScsMships(sc?.id ?? 0, queryParams) as Promise<
                ListResponse<AutocompleteOption>
            >,
    });

    return (
        <Autocomplete
            options={
                scsMshipsQuery?.data?.rows
                    ? scsMshipsQuery?.data?.rows
                    : [
                          {
                              id: value?.id,
                              name: value?.name,
                          },
                      ]
            }
            loading={scsMshipsQuery?.isLoading}
            getOptionLabel={(option: AutocompleteOption) =>
                option?.name ? option?.name : ''
            }
            isOptionEqualToValue={(
                option: AutocompleteOption,
                val: AutocompleteOption,
            ) => option?.id === val?.id}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    label={caption}
                    size="small"
                    variant="outlined"
                    error={!!errors[name]}
                    helperText={
                        errors[name] ? String(errors[name]?.message) : null
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {scsMshipsQuery?.isLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option: AutocompleteOption) => (
                <li {...props}>
                    <ListItemAvatar>
                        <Avatar
                            alt={option.name}
                            src={option.img ? option.img.url : undefined}
                        />
                    </ListItemAvatar>

                    <ListItemText
                        primary={option.fullName}
                        secondary={(() => {
                            if (option.user && checkOffTag(option.user.login)) {
                                return (
                                    <i style={{ color: '#5b5b5b' }}>
                                        {translate('UserCancelled')}
                                    </i>
                                );
                            }

                            if (option.amount) {
                                return (
                                    <>
                                        <span>{translate('Purse')}:&nbsp;</span>
                                        <b>{option.amount}€</b>
                                    </>
                                );
                            }

                            return null;
                        })()}
                    />
                </li>
            )}
            onChange={(_e, data) => onChange(data)}
            onInputChange={(_event, newInputValue) =>
                setQueryParams({
                    ...queryParams,
                    name: newInputValue,
                })
            }
            value={value || null}
            style={{ paddingTop: 16 }}
            {...rest}
        />
    );
}

function SttAutocompleteProvince({
    name,
    caption,
    value,
    errors,
    onChange,
    ...rest
}: AutocompleteComponentProps) {
    const provincesService = useProvincesService();
    const [queryParams, setQueryParams] = useState({
        idCountry: 724,
        rows: 100,
        name: '',
    });

    const provincesQuery = useQuery({
        queryKey: [SELECT_PROVINCES, queryParams],
        queryFn: () =>
            provincesService.getList<ListResponse<AutocompleteOption>>(
                queryParams,
            ),
    });

    return (
        <Autocomplete
            options={
                provincesQuery?.data?.rows
                    ? provincesQuery?.data?.rows
                    : [
                          {
                              id: value?.id,
                              name: value?.name,
                          },
                      ]
            }
            loading={provincesQuery?.isLoading}
            getOptionLabel={(option: AutocompleteOption) =>
                option?.name ? option?.name : ''
            }
            isOptionEqualToValue={(
                option: AutocompleteOption,
                val: AutocompleteOption,
            ) => option?.id === val?.id}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    label={caption}
                    size="small"
                    variant="outlined"
                    error={!!errors[name]}
                    helperText={
                        errors[name] ? String(errors[name]?.message) : null
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {provincesQuery?.isLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onChange={(_e, data) => onChange(data)}
            onInputChange={(_event, newInputValue) =>
                setQueryParams({
                    ...queryParams,
                    name: newInputValue,
                })
            }
            value={value || null}
            style={{ paddingTop: 16 }}
            {...rest}
        />
    );
}

function SttValidatedForm({
    form,
    loadingData = false,
    fields = [],
    buttons = [],
    containerProps = {},
}: SttValidatedFormProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    const {
        control,
        formState: { errors },
    } = form;

    const renderTextInput = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, onEnterKey, ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={{
                    ...rules,
                    ...(rules?.required === true
                        ? { required: t('mandatory') }
                        : undefined),
                }}
                render={({ field: { onChange, value } }) => (
                    <TextField
                        variant="outlined"
                        size="small"
                        margin="normal"
                        fullWidth
                        id={name}
                        label={caption}
                        name={name}
                        onChange={onChange}
                        error={!!errors[name]}
                        helperText={(() => {
                            if (errors[name]) {
                                return String(errors[name]?.message);
                            }

                            if (rules) {
                                return ' ';
                            }

                            return null;
                        })()}
                        value={value || ''}
                        onKeyPress={(ev: any) => {
                            if (ev.key === 'Enter') {
                                onEnterKey?.(value);
                            }
                        }}
                        {...rest}
                    />
                )}
            />
        );
    };

    const renderNumberInput = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, onEnterKey, ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={{
                    pattern: {
                        value: /^(0|[1-9]\d*)(\.\d+)?$/,
                        message: t('mustBeNumber'),
                    },
                    ...rules,
                    ...(rules?.required === true
                        ? { required: t('mandatory') }
                        : undefined),
                }}
                render={({ field: { onChange, value } }) => (
                    <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        fullWidth
                        id={name}
                        label={caption}
                        name={name}
                        onChange={onChange}
                        error={!!errors[name]}
                        helperText={(() => {
                            if (errors[name]) {
                                return String(errors[name]?.message);
                            }

                            if (rules) {
                                return ' ';
                            }

                            return null;
                        })()}
                        value={value || ''}
                        onKeyPress={(ev: any) => {
                            if (ev.key === 'Enter') {
                                onEnterKey?.(value);
                            }
                        }}
                        {...rest}
                    />
                )}
            />
        );
    };

    const renderSelector = (field: FormField, fieldIndex: number) => {
        const {
            name,
            caption,
            rules,
            options = [],
            value: _fieldValue,
            _disableClearable,
            ...rest
        } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => {
                    const selectedValue: FieldOption | null =
                        options && value !== undefined
                            ? options.find(
                                  (option: any) => option.value === value,
                              ) || null
                            : options[0] || null;
                    return (
                        <Autocomplete<FieldOption, false, false, false>
                            options={options}
                            getOptionLabel={(option: FieldOption) =>
                                option
                                    ? option.caption || option.label || ''
                                    : ''
                            }
                            renderInput={(params: any) => (
                                <TextField
                                    {...params}
                                    label={caption}
                                    size="small"
                                    variant="outlined"
                                    error={!!errors[name]}
                                    helperText={
                                        errors[name]
                                            ? String(errors[name]?.message)
                                            : null
                                    }
                                />
                            )}
                            onChange={(_e, data: FieldOption | null) => {
                                onChange(data?.value);
                            }}
                            value={selectedValue}
                            style={{ paddingTop: 16 }}
                            {...(rest as any)}
                        />
                    );
                }}
            />
        );
    };

    const renderTextArea = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, rows = 1, ...rest } = field;

        return loadingData ? (
            <Skeleton
                variant="rectangular"
                height={rows * textAreaHeightPerRow}
            />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <TextField
                        fullWidth
                        multiline
                        variant="outlined"
                        margin="normal"
                        size="small"
                        id={name}
                        label={caption}
                        name={name}
                        minRows={rows || 3}
                        maxRows={rows || 3}
                        onChange={onChange}
                        error={!!errors[name]}
                        helperText={
                            errors[name] ? String(errors[name]?.message) : null
                        }
                        value={value || ''}
                        {...rest}
                    />
                )}
            />
        );
    };

    const renderDate = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, value: _fieldValue, ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => {
                    // Parse value to Date object
                    const parsedValue = (() => {
                        if (!value) return null;
                        try {
                            const parsed = parseISO(value as string);
                            return isValid(parsed) ? parsed : null;
                        } catch {
                            return null;
                        }
                    })();

                    return (
                        <DatePicker
                            slotProps={{
                                textField: {
                                    variant: 'outlined',
                                    size: 'small',
                                    style: { width: '100%' },
                                    margin: 'normal',
                                    id: name,
                                    error: !!errors[name],
                                    helperText: errors[name]
                                        ? String(errors[name]?.message)
                                        : null,
                                },
                                actionBar: {
                                    actions: ['clear', 'cancel', 'accept'],
                                },
                            }}
                            format="dd/MM/yyyy"
                            label={caption}
                            value={parsedValue}
                            onChange={(date: Date | null) => {
                                if (date && isValid(date)) {
                                    onChange(format(date, 'yyyy-MM-dd'));
                                } else {
                                    onChange(null);
                                }
                            }}
                            {...rest}
                        />
                    );
                }}
            />
        );
    };

    const renderCheckBox = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <FormControl component="fieldset">
                <FormControlLabel
                    control={
                        <Controller
                            key={fieldIndex}
                            name={name}
                            control={control}
                            rules={rules}
                            render={(props) => (
                                <Checkbox
                                    checked={!!props.field.value}
                                    onChange={props.field.onChange}
                                    name={name}
                                    disabled={
                                        rest.disabled ? rest.disabled : false
                                    }
                                />
                            )}
                        />
                    }
                    label={caption || ''}
                />
                <FormHelperText error={!!errors[name]}>
                    {errors[name] ? String(errors[name]?.message) : null}
                </FormHelperText>
            </FormControl>
        );
    };

    const renderCity = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, idProvince, ...rest } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteCity
                        name={name}
                        caption={caption}
                        value={value as AutocompleteOption | null}
                        errors={errors}
                        onChange={onChange}
                        idProvince={idProvince}
                        rest={rest}
                    />
                )}
            />
        );
    };

    const renderPrice = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <TextField
                        variant="outlined"
                        margin="normal"
                        size="small"
                        fullWidth
                        id={name}
                        label={caption}
                        name={name}
                        onChange={onChange}
                        error={!!errors[name]}
                        helperText={
                            errors[name] ? String(errors[name]?.message) : null
                        }
                        value={value || ''}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    €
                                </InputAdornment>
                            ),
                        }}
                        {...rest}
                    />
                )}
            />
        );
    };

    const renderCustomer = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, ...rest } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteCustomer
                        name={name}
                        caption={caption}
                        value={value as AutocompleteOption | null}
                        errors={errors}
                        onChange={onChange}
                        rest={rest}
                    />
                )}
            />
        );
    };

    const renderTpvConfiguration = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, ...rest } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteTpvConfiguration
                        name={name}
                        caption={caption}
                        value={value as AutocompleteOption | null}
                        errors={errors}
                        onChange={onChange}
                        rest={rest}
                    />
                )}
            />
        );
    };

    const renderLabel = (field: FormField, fieldIndex: number) => {
        const { caption, name, rules, formatedText = false } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { value } }) => (
                    <div key={fieldIndex}>
                        <div>
                            <Typography variant="caption">{caption}</Typography>
                        </div>
                        {formatedText ? (
                            <Box style={{ overflow: 'auto' }}>
                                <pre>
                                    <b>{String(value)}</b>
                                </pre>
                            </Box>
                        ) : (
                            <b>{String(value)}</b>
                        )}
                    </div>
                )}
            />
        );
    };

    const renderTime = (field: FormField, fieldIndex: number) => {
        const { name, caption, rules, value: _fieldValue, ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => {
                    // Parse time string to Date object
                    const parsedValue = (() => {
                        if (!value) return null;
                        const parts = String(value).split(':');
                        const hours = parseInt(parts[0] ?? '0', 10);
                        const minutes = parseInt(parts[1] ?? '0', 10);
                        return new Date(1970, 0, 1, hours, minutes, 0);
                    })();

                    return (
                        <TimePicker
                            ampm={false}
                            label={caption}
                            value={parsedValue}
                            onChange={(date: Date | null) => {
                                if (date && isValid(date)) {
                                    onChange(format(date, 'H:mm'));
                                } else {
                                    onChange(null);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    variant: 'outlined',
                                    size: 'small',
                                    style: { width: '100%' },
                                    margin: 'normal',
                                    id: name,
                                    error: !!errors[name],
                                    helperText: errors[name]
                                        ? String(errors[name]?.message)
                                        : null,
                                },
                            }}
                            {...rest}
                        />
                    );
                }}
            />
        );
    };

    const renderMultipleSelectorCheck = (
        field: FormField,
        fieldIndex: number,
    ) => {
        const { name, caption, rules, options = [], ...rest } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <FormControl
                        variant="outlined"
                        size="small"
                        margin="normal"
                        fullWidth
                    >
                        <InputLabel>{caption}</InputLabel>
                        <Select
                            multiple
                            variant="outlined"
                            label={caption}
                            value={
                                options && value !== undefined
                                    ? (value as string[])
                                    : []
                            }
                            onChange={(data: any) =>
                                onChange(data?.target?.value)
                            }
                            renderValue={(selected) =>
                                (selected as string[])
                                    .map(
                                        (a) =>
                                            options.find(
                                                (o: any) => o?.value === a,
                                            )?.caption,
                                    )
                                    .join(', ')
                            }
                            {...rest}
                        >
                            {options.map((option: any) => (
                                <MenuItem
                                    key={String(option?.value)}
                                    value={option?.value}
                                >
                                    <Checkbox
                                        checked={
                                            value !== undefined &&
                                            (value as (string | number)[])
                                                .map((a: any) => a)
                                                .includes(option?.value)
                                        }
                                    />
                                    <ListItemText primary={option?.caption} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            />
        );
    };

    const renderMonths = (field: FormField, fieldIndex: number) => {
        const { name, rules, tinyLabels = true } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <ButtonGroup>
                        {(
                            cxt.constants.months as {
                                id: number;
                                tiny: string;
                                short: string;
                            }[]
                        ).map((month: any) => (
                            <Button
                                key={month.id}
                                variant={
                                    Array.isArray(value) &&
                                    (value as number[])
                                        .map((a: any) => a)
                                        .includes(month?.id)
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color={
                                    Array.isArray(value) &&
                                    (value as number[])
                                        .map((a: any) => a)
                                        .includes(month?.id)
                                        ? 'primary'
                                        : 'inherit'
                                }
                                onClick={() => {
                                    const updatedMonths = [
                                        ...(value as number[]),
                                    ];
                                    const indexValue = (
                                        value as number[]
                                    ).findIndex((m) => m === month?.id);

                                    if (indexValue > -1) {
                                        updatedMonths.splice(indexValue, 1);
                                        onChange(updatedMonths);
                                    } else {
                                        updatedMonths.push(month?.id);
                                        onChange(updatedMonths);
                                    }
                                }}
                            >
                                {tinyLabels ? month.tiny : month.short}
                            </Button>
                        ))}
                    </ButtonGroup>
                )}
            />
        );
    };

    const renderWeekdays = (field: FormField, fieldIndex: number) => {
        const { name, rules, tinyLabels = true } = field;

        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <ButtonGroup>
                        {(
                            cxt.constants.weekdays as {
                                id: number;
                                tiny: string;
                                short: string;
                            }[]
                        ).map((weekday: any) => (
                            <Button
                                key={weekday.id}
                                variant={
                                    Array.isArray(value) &&
                                    (value as number[])
                                        .map((a: any) => a)
                                        .includes(weekday?.id)
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color={
                                    Array.isArray(value) &&
                                    (value as number[])
                                        .map((a: any) => a)
                                        .includes(weekday?.id)
                                        ? 'primary'
                                        : 'inherit'
                                }
                                onClick={() => {
                                    const updatedMonths = [
                                        ...(value as number[]),
                                    ];
                                    const indexValue = (
                                        value as number[]
                                    ).findIndex((m) => m === weekday?.id);

                                    if (indexValue > -1) {
                                        updatedMonths.splice(indexValue, 1);
                                        onChange(updatedMonths);
                                    } else {
                                        updatedMonths.push(weekday?.id);
                                        onChange(updatedMonths);
                                    }
                                }}
                            >
                                {tinyLabels ? weekday.tiny : weekday.short}
                            </Button>
                        ))}
                    </ButtonGroup>
                )}
            />
        );
    };

    const renderGroup = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, ...rest } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteGroup
                        name={name}
                        caption={caption}
                        value={value}
                        errors={errors}
                        onChange={onChange}
                        rest={rest}
                    />
                )}
            />
        );
    };

    const renderCountry = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, value: defValue, ...rest } = field;
        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteCountry
                        name={name}
                        caption={caption}
                        value={
                            value as
                                | { id: number; name: string }
                                | null
                                | undefined
                        }
                        defValue={
                            defValue as
                                | { id: number; name: string }
                                | null
                                | undefined
                        }
                        errors={errors}
                        onChange={onChange}
                        rest={rest}
                    />
                )}
            />
        );
    };

    const renderMap = (field: FormField, fieldIndex: number) => {
        const { name, rules, value: defValue } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteMap
                        value={value as { mapX?: number; mapY?: number } | null}
                        defValue={
                            defValue as { mapX?: number; mapY?: number } | null
                        }
                        onChange={onChange}
                    />
                )}
            />
        );
    };
    const renderMship = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, ...rest } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteScsMships
                        name={name}
                        caption={caption}
                        value={value as AutocompleteOption | null}
                        errors={errors}
                        onChange={onChange}
                        rest={rest}
                    />
                )}
            />
        );
    };

    const renderProvince = (field: FormField, fieldIndex: number) => {
        const { name, caption = '', rules, ...rest } = field;

        // He añadido un padding de 16px al componente Autocomplete para que quede centrado, no he doy con una solución
        // bonita.
        return loadingData ? (
            <Skeleton variant="rectangular" height={textHeight} />
        ) : (
            <Controller
                key={fieldIndex}
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <SttAutocompleteProvince
                        name={name}
                        caption={caption}
                        value={value as AutocompleteOption | null}
                        errors={errors}
                        onChange={onChange}
                        rest={rest}
                    />
                )}
            />
        );
    };

    /**
     * Render form buttons.
     * @param button Button object.
     * @param buttonIndex Button index.
     * @returns {React.ReactElement}
     */
    const renderButton = (button: FormButton, buttonIndex: number) => {
        let content;
        const {
            caption,
            onClick,
            show = true,
            type,
            confirmation = false,
            xs = 12,
            sm = 12,
            md = 3,
            lg,
            xl,
            permission,
        } = button;

        if (permission && !can(permission)) return null;

        switch (type) {
            case 'save':
                content = (
                    <SttButtonSave
                        fullWidth
                        caption={caption}
                        onClick={onClick}
                        confirmation={confirmation}
                    />
                );
                break;
            case 'delete':
                content = (
                    <SttButtonDelete
                        fullWidth
                        caption={caption}
                        onClick={onClick}
                        confirmation
                    />
                );
                break;
            case 'accept':
                content = (
                    <SttButtonAccept
                        fullWidth
                        caption={caption}
                        onClick={onClick}
                        confirmation={confirmation}
                    />
                );
                break;
            case 'recover':
                content = (
                    <SttButtonRecover
                        fullWidth
                        caption={caption}
                        onClick={onClick}
                        confirmation={confirmation}
                    />
                );
                break;
            case 'component':
                content = button.component;
                break;
            default:
                content = (
                    <SttButton
                        fullWidth
                        caption={caption}
                        onClick={onClick}
                        confirmation={confirmation}
                    />
                );
        }

        if (show) {
            return (
                <Grid key={buttonIndex} size={{ xs, sm, md, lg, xl }}>
                    {content}
                </Grid>
            );
        }
        return null;
    };

    const renderFormField = (field: FormField, fieldIndex: number) => {
        switch (field?.type ? field?.type.toLowerCase() : 'textinput') {
            case 'textinput':
                return renderTextInput(field, fieldIndex);
            case 'numberinput':
                return renderNumberInput(field, fieldIndex);
            case 'select':
                return renderSelector(field, fieldIndex);
            case 'textarea':
                return renderTextArea(field, fieldIndex);
            case 'date':
                return renderDate(field, fieldIndex);
            case 'check':
                return renderCheckBox(field, fieldIndex);
            case 'city':
                return renderCity(field, fieldIndex);
            case 'price':
                return renderPrice(field, fieldIndex);
            case 'customer':
                return renderCustomer(field, fieldIndex);
            case 'tpvconf':
                return renderTpvConfiguration(field, fieldIndex);
            case 'label':
                return renderLabel(field, fieldIndex);
            case 'time':
                return renderTime(field, fieldIndex);
            case 'multipleselectcheck':
                return renderMultipleSelectorCheck(field, fieldIndex);
            case 'months':
                return renderMonths(field, fieldIndex);
            case 'weekdays':
                return renderWeekdays(field, fieldIndex);
            case 'group':
                return renderGroup(field, fieldIndex);
            case 'mship':
                return renderMship(field, fieldIndex);
            case 'province':
                return renderProvince(field, fieldIndex);
            case 'country':
                return renderCountry(field, fieldIndex);
            case 'map':
                return renderMap(field, fieldIndex);
            default:
                return null;
        }
    };

    const renderFormFieldGridItem = (field: FormField, fieldIndex: number) => {
        const { show = true, xs = 12, sm, md, lg, xl } = field;

        if (show) {
            return (
                <Grid key={fieldIndex} size={{ xs, sm, md, lg, xl }}>
                    {renderFormField(field, fieldIndex)}
                </Grid>
            );
        }
        return null;
    };

    return (
        <form onSubmit={(e: any) => e.preventDefault()}>
            <Grid container spacing={2} {...containerProps}>
                {fields.map((field: any, fieldIndex: any) =>
                    renderFormFieldGridItem(field, fieldIndex),
                )}
            </Grid>

            {buttons && (
                <Box mt={4}>
                    <Grid container spacing={3}>
                        {buttons.map(renderButton)}
                    </Grid>
                </Box>
            )}
        </form>
    );
}

export default SttValidatedForm;
